import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { formatDate } from '../lib/dates'
import { formatCurrency } from '../lib/jobFinancials'

// ─── Proposals List ─────────────────────────────────────────────────────────
// Closes a completeness gap flagged in Session 3.2 (see Docs/ROADMAP.md Phase 3):
// once a proposal is generated from EstimateBuilder and you navigate away from its
// preview (/proposals/:id), there was previously no way to find it again except the
// exact URL or the browser back button. This gives proposals the same list-page
// pattern already established by Estimates.js and Invoices.js's InvoicesList.
//
// Data model: Docs/DECISIONS_LOG.md FA-012 — `proposals` is one row per version.
// Default view shows only the current version of each proposal group
// (is_current_version = true) per Docs/QUOTE_TO_CASH.md "Proposal Versions" —
// superseded versions are hidden unless "Show Superseded Versions" is toggled on,
// where they're marked "Outdated" rather than cluttering the default list.

const PROPOSAL_STATUS_COLOR = { draft: '#6b7280', sent: '#2563eb', viewed: '#7c3aed', approved: '#16a34a', declined: '#dc2626', expired: '#6b7280', superseded: '#9ca3af' }
const PROPOSAL_STATUS_BG    = { draft: '#f3f4f6', sent: '#dbeafe', viewed: '#ede9fe', approved: '#dcfce7', declined: '#fef2f2', expired: '#f3f4f6', superseded: '#f3f4f6' }

export default function Proposals() {
  const navigate = useNavigate()
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAllVersions, setShowAllVersions] = useState(false)

  useEffect(() => { fetchProposals() }, [showAllVersions])

  // Only the columns the list actually renders — `proposals` also carries large text/JSON
  // snapshot columns (sections_data, visibility_settings, etc.) this page never needs.
  // Default view fetches current-version rows only; toggling "Show Superseded" refetches
  // everything, rather than always pulling full version history on every page load.
  async function fetchProposals() {
    setLoading(true)
    let query = supabase
      .from('proposals')
      .select('id, proposal_number, status, selected_contract_total, tax_amount, created_at, version_number, is_current_version, client:clients(name)')
      .order('created_at', { ascending: false })
    if (!showAllVersions) query = query.eq('is_current_version', true)
    const { data, error } = await query
    if (error) setError('Failed to load proposals: ' + error.message)
    else setProposals(data || [])
    setLoading(false)
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Proposals</h1>
          <p style={s.sub}>{proposals.length} {showAllVersions ? 'total' : 'current'} · Customer-facing proposals generated from estimates</p>
        </div>
        <div style={s.headerActions}>
          <button
            style={{ ...s.toggleBtn, ...(showAllVersions ? s.toggleBtnActive : {}) }}
            onClick={() => setShowAllVersions(v => !v)}
          >
            {showAllVersions ? '✓ Showing All Versions' : 'Show Superseded Versions'}
          </button>
          <button style={s.addBtn} onClick={() => navigate('/estimates')}>+ Generate from Estimate</button>
        </div>
      </div>

      {error && (
        <div style={s.errorBox}>
          <span>{error}</span>
          <button style={s.errorClose} onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading proposals...</div>
      ) : proposals.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
          <div style={{ fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            {showAllVersions ? 'No proposals yet' : 'No current proposals'}
          </div>
          <div style={{ color: '#6b7c6b', fontSize: '14px', marginBottom: '20px' }}>Generate a proposal from an estimate to see it here</div>
          <button style={s.addBtn} onClick={() => navigate('/estimates')}>Go to Estimates</button>
        </div>
      ) : (
        <div style={s.list}>
          {proposals.map(p => {
            const total = (p.selected_contract_total || 0) + (p.tax_amount || 0)
            return (
              <div key={p.id} style={s.card} onClick={() => navigate(`/proposals/${p.id}`)}>
                <div style={s.cardLeft}>
                  <div style={s.propRow}>
                    <span style={s.propTitle}>{p.proposal_number} — {p.client?.name || 'No client'}</span>
                    <span style={{ ...s.statusBadge, background: PROPOSAL_STATUS_BG[p.status] || '#f3f4f6', color: PROPOSAL_STATUS_COLOR[p.status] || '#6b7280' }}>{p.status}</span>
                    {!p.is_current_version && <span style={s.outdatedBadge}>Outdated · v{p.version_number}</span>}
                  </div>
                  <span style={s.propDate}>{formatDate(p.created_at)}</span>
                </div>
                <div style={s.cardRight}>
                  <span style={s.propTotal}>{formatCurrency(total)}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const s = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  headerActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  toggleBtn: { background: '#f3f4f6', border: '1.5px solid #d1d5db', color: '#374151', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  toggleBtnActive: { background: '#ede9fe', border: '1.5px solid #c4b5fd', color: '#6d28d9' },
  addBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '14px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  errorClose: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '0 4px', fontFamily: 'inherit', flexShrink: 0 },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  empty: { textAlign: 'center', padding: '60px 0', color: '#6b7c6b' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  card: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', borderRadius: '10px', padding: '16px 20px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', gap: '12px', flexWrap: 'wrap', cursor: 'pointer' },
  cardLeft: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 },
  propRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  propTitle: { fontWeight: '600', color: '#1a2e1a', fontSize: '15px' },
  propDate: { fontSize: '12px', color: '#9ca3af' },
  cardRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' },
  propTotal: { fontWeight: '700', color: '#2d4a2d', fontSize: '20px' },
  statusBadge: { padding: '3px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', textTransform: 'capitalize' },
  outdatedBadge: { padding: '3px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' },
}
